import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Star } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useRoleBase } from "../../hooks/useRoleBase";
import { Card, PageHeader, EmptyState, Spinner } from "../../components/dashboard/DashboardShell";
import { fmtDate } from "../../lib/format";

export default function ReviewsPage() {
  const { user } = useAuth();
  const base = useRoleBase();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["reviews-me"], queryFn: () => api.get("/reviews/me").then((r) => r.data) });
  const isInsp = user.role === "inspector";
  const avg = data?.length ? (data.reduce((s, r) => s + r.rating, 0) / data.length).toFixed(1) : "0.0";
  const breakdown = [5, 4, 3, 2, 1].map((s) => ({ s, n: data?.filter((r) => r.rating === s).length || 0 }));
  return (
    <div>
      <PageHeader title={isInsp ? "Rating & Ulasan" : "Ulasan Saya"} subtitle={isInsp ? "Ulasan yang diberikan pengguna atas layanan Anda." : "Ulasan yang telah Anda berikan kepada inspector."} />
      <div className="grid xl:grid-cols-3 gap-4">
        {isInsp && (
          <Card>
            <p className="text-xs font-semibold text-slate-400">Rating Rata-rata</p>
            <div className="flex items-center gap-2 mt-1"><Star size={24} className="text-amber-400" fill="#fbbf24" /><p className="text-3xl font-extrabold text-slate-900" data-testid="avg-rating">{avg}</p><span className="text-slate-400 text-sm">/ 5</span></div>
            <p className="text-xs text-slate-400 mt-1">{data?.length || 0} ulasan</p>
            <div className="mt-4 space-y-1.5">
              {breakdown.map((b) => (
                <div key={b.s} className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-500 w-2">{b.s}</span><Star size={10} className="text-amber-400" fill="#fbbf24" />
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-600 rounded-full" style={{ width: `${data?.length ? (b.n / data.length) * 100 : 0}%` }} /></div>
                  <span className="text-[11px] text-slate-400 w-6 text-right">{b.n}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
        <Card className={isInsp ? "xl:col-span-2" : "xl:col-span-3"}>
          {isLoading ? <Spinner /> : !data?.length ? <EmptyState icon="star" title="Belum ada ulasan" /> : (
            <div className="divide-y divide-slate-50" data-testid="reviews-list">
              {data.map((r) => {
                const p = isInsp ? r.user : r.inspector;
                return (
                  <div key={r.review_id} className="flex gap-3 py-4" data-testid={`review-${r.review_id}`}>
                    <img src={p?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(p?.name || "U")}&background=2563eb&color=fff`} alt="" className="w-10 h-10 rounded-full object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-[13px] font-bold text-slate-900">{p?.name}</p>
                        <span className="text-[11px] text-slate-400">{fmtDate(r.created_at)}</span>
                      </div>
                      <p className="text-amber-400 text-xs mt-0.5">{"★".repeat(r.rating)}<span className="text-slate-200">{"★".repeat(5 - r.rating)}</span></p>
                      <p className="text-sm text-slate-600 mt-1.5">{r.comment || <i className="text-slate-400">Tanpa komentar</i>}</p>
                      <button onClick={() => navigate(`${base}/pesanan/${r.order_id}`)} className="text-[11px] font-bold text-blue-600 mt-1.5">{r.vehicle_type_label} · {r.order_id} →</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
